import "./polyfills";
import { createApp } from "vue";
import { createPinia } from "pinia";
import App from "./App.vue";
import router from "./router";
import "./styles/main.css";
import { initDb } from "./db/sqlite";
import { useUserStore } from "./stores/user";
import { useNetwork } from "./composables/useNetwork";
import { syncProgress } from "./services/syncProgress";
import { getApi } from "./api";
import { initSentry } from "./sentry";

async function attemptSync() {
  const user = useUserStore();
  if (!user.isAuthenticated) return;
  try {
    const res = await syncProgress(getApi());
    if (res.synced > 0) console.info(`synced ${res.synced} events`);
  } catch (err) {
    console.warn("sync failed; events remain queued", err);
  }
}

async function bootstrap() {
  const app = createApp(App);
  const pinia = createPinia();
  app.use(pinia);

  // Restore the persisted session BEFORE installing the router. app.use(router)
  // triggers Vue Router's initial navigation, which resolves "/" and runs the
  // auth guard. On native the session lives in Capacitor Preferences and can
  // only be read asynchronously (hydrate), so installing the router first lets
  // that initial navigation resolve while the user store is still empty —
  // redirecting an already-signed-in user to /welcome on every cold start, the
  // whole funnel again. Hydrating first means the initial navigation sees the
  // real auth state. Web was never affected because the store seeds
  // synchronously from localStorage, which is exactly why this hid through
  // native testing until a force-quit + relaunch was tried on a device.
  const user = useUserStore();
  try { await initDb(); } catch (err) { console.warn("SQLite init failed", err); }
  await user.hydrate();

  app.use(router);
  initSentry(app, router);

  const network = useNetwork({ onReconnect: attemptSync });
  app.mount("#app");

  if (network.isOnline.value) void attemptSync();
}

bootstrap();
